import { toast } from "react-toastify";
import React, { useEffect, useMemo, useState } from "react";
import taskService from "../../../shared/api/animal/taskService";
import { listBatchesIdName } from "../../../shared/api/animal/vaccineService";

const SPECIES_OPTIONS = ["GOAT", "SHEEP", "CATTLE", "CAMEL"];
const PROJECT_TYPE_OPTIONS = ["FATTENING", "BREEDING", "DAIRY"];
const TASK_STATUS_OPTIONS = ["", "PENDING", "GIVEN", "NOT_GIVEN", "SKIPPED", "RESCHEDULED"];
const VACCINE_STATUS_OPTIONS = ["", "PENDING", "UPCOMING", "GIVEN", "SKIPPED"];
const CONDITION_OPTIONS = ["BETTER", "SAME", "WORSE"];

const TABS = [
  { key: "vaccine", label: "Vaccine" },
  { key: "feeding", label: "Food / Feeding" },
  { key: "medicine", label: "Medicine" },
];

const REPORT_MODES = [
  { value: "selected", label: "Selected Date" },
  { value: "today", label: "Present / Today" },
  { value: "allPast", label: "All Past" },
  { value: "pastDate", label: "Selected Past Date" },
  { value: "allFuture", label: "All Future" },
  { value: "futureDate", label: "Selected Future Date" },
  { value: "all", label: "All Days" },
];

function todayStr() {
  // Local calendar date; UTC ISO can roll into the previous day in India.
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
}

function errMsg(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "string" ? error.response.data : null) ||
    error?.message ||
    "Something went wrong"
  );
}

function formatDate(v) {
  return v ? String(v).slice(0, 10) : "-";
}

function animalIdentity(task) {
  return task.assetId || task.animalId || task.livestockId || task.animalTag || task.animalName || "all";
}

function combineTaskRows(tasks, type) {
  const groups = new Map();
  (tasks || []).forEach((task) => {
    const commonId = type === "medicine"
      ? (task.medicineGroupId || task.treatmentRequestId || task.healthTreatmentId || task.diseaseCaseId || task.requestKey || task.notes)
      : (task.vaccineGroupId || task.vaccinationRuleId || task.groupId || task.scheduleRequestKey || task.requestKey || task.notes || "same-vaccine-allocation");
    const key = [type, formatDate(task.dueDate || task.taskDate || task.date), task.batchId || task.batchName,
      animalIdentity(task), task.taskType || "", task.status || "", commonId || `single-${task.id}`].join("|");
    if (!groups.has(key)) groups.set(key, { ...task, combinationKey: key, taskIds: [], components: [] });
    const group = groups.get(key);
    group.taskIds.push(task.id);
    group.components.push(task);
  });
  return [...groups.values()].map((group) => {
    const names = [...new Set(group.components.map((item) => type === "medicine"
      ? (item.itemName || item.medicineName)
      : item.vaccineName).filter(Boolean))];
    return { ...group, combinationName: names.join(" + ") || "-" };
  });
}

function getBatchName(batchId, batches) {
  const match = (batches || []).find((b) => String(b.id) === String(batchId));
  return match?.batchName || match?.name || `Batch ${batchId}`;
}

function taskStatusChip(status) {
  const map = {
    PENDING: { background: "#fff7df", color: "#805b12", border: "1px solid #efd38b" },
    GIVEN: { background: "#e9f6ef", color: "#116b4e", border: "1px solid #9fd4bb" },
    NOT_GIVEN: { background: "#fff0f0", color: "#a43b3b", border: "1px solid #efb1b1" },
    SKIPPED: { background: "#f1f4f2", color: "#52645a", border: "1px solid #cad7ce" },
    RESCHEDULED: { background: "#edf4fa", color: "#326b8f", border: "1px solid #afc9dc" },
    COMPLETED: { background: "#e9f6ef", color: "#116b4e", border: "1px solid #9fd4bb" },
    UPCOMING: { background: "#f1f4f2", color: "#52645a", border: "1px solid #cad7ce" },
    TODAY: { background: "#edf4fa", color: "#326b8f", border: "1px solid #afc9dc" },
    MISSED: { background: "#fff0f0", color: "#a43b3b", border: "1px solid #efb1b1" },

  };
  return map[status] || { background: "#f1f4f2", color: "#52645a", border: "1px solid #cad7ce" };
}

function TaskStatusBadge({ status }) {
  return (
    <span
      style={{
        ...styles.badge,
        ...taskStatusChip(status),
      }}
    >
      {status || "-"}
    </span>
  );
}


function escapeHtml(value) {
  return String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function reportDateOf(row, type) {
  if (type === "vaccine") return formatDate(row.taskDate || row.date);
  return formatDate(row.dueDate || row.taskDate || row.feedDate || row.logDate || row.date);
}

function filterRowsByReportMode(rows, type, mode, selectedDate) {
  const today = todayStr();
  const target = mode === "today" ? today : (selectedDate || today);
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const rowDate = reportDateOf(row, type);
    if (!rowDate || rowDate === "-") return mode === "all";
    if (mode === "all") return true;
    if (["selected", "pastDate", "futureDate", "today"].includes(mode)) return rowDate === target;
    if (mode === "allPast") return rowDate < today;
    if (mode === "allFuture") return rowDate > today;
    return true;
  });
}

function reportRows(type, rows, batches) {
  if (type === "vaccine") {
    return (rows || []).map((r) => [
      reportDateOf(r, type),
      r.batchName || getBatchName(r.batchId, batches),
      r.animalId || r.assetId || r.livestockId || "-",
      r.vaccineName || "-",
      r.taskType || "-",
      r.status || "-",
      r.notes || "-",
    ]);
  }
  if (type === "feeding") {
    return (rows || []).map((r) => [
      reportDateOf(r, type),
      r.batchName || getBatchName(r.batchId, batches),
      r.assetId || r.animalId || r.livestockId || "-",
      r.components?.length ? r.components.map(c => `${c.inventoryName}: ${c.percentage}%`).join(" + ") : (r.itemName || r.inventoryName || "Feed"),
      r.plannedTotalGrams != null ? `${r.plannedTotalGrams} g` : (r.plannedQuantity != null ? `${r.plannedQuantity} g` : "-"),
      r.components?.length ? r.components.map(c => `${c.inventoryName}: ${c.actualGrams ?? c.plannedGrams} g`).join(" + ") : (r.actualInventoryQuantity != null ? `${Number(r.actualInventoryQuantity).toLocaleString(undefined, { maximumFractionDigits: 3 })} ${r.unit || ""}`.trim() : (r.actualQuantity != null ? `${r.actualQuantity} g` : "-")),
      r.status || "-",
    ]);
  }
  return (rows || []).map((r) => [
    reportDateOf(r, type),
    r.batchName || getBatchName(r.batchId, batches),
    r.assetId || r.livestockId || "-",
    r.itemName || r.medicineName || "-",
    r.plannedQuantity != null ? `${r.plannedQuantity} ${r.unit || ""}`.trim() : "-",
    r.medicineRoute || r.route || "-",
    r.status || (r.medicineGivenToday ? "GIVEN" : "-"),
    r.notes || "-",
  ]);
}

function reportHeaders(type) {
  if (type === "vaccine") return ["Date", "Batch", "Animal", "Vaccine", "Type", "Status", "Notes"];
  if (type === "feeding") return ["Date", "Batch", "Animal", "Feed Item", "Planned", "Actual", "Status"];
  return ["Date", "Batch", "Asset", "Medicine", "Planned", "Route", "Status", "Notes"];
}

function titleForType(type) {
  if (type === "vaccine") return "Vaccine Task Report";
  if (type === "feeding") return "Food / Feeding Task Report";
  return "Medicine Task Report";
}

function openPrintableReport({ title, subtitle, sections }) {
  const createdOn = new Date().toLocaleString("en-IN");
  const sectionHtml = sections.map((section, index) => {
    const headers = reportHeaders(section.type);
    const rows = reportRows(section.type, section.rows, section.batches);
    const body = rows.length
      ? rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")
      : `<tr><td colspan="${headers.length}" class="empty">No records found</td></tr>`;
    return `
      <section class="report-section">
        <h2><span>${index + 1}</span> ${escapeHtml(section.title)}</h2>
        <div class="count">Total rows: ${rows.length}</div>
        <table>
          <thead><tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
          <tbody>${body}</tbody>
        </table>
      </section>`;
  }).join("");

  const html = `<!doctype html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <style>
      @page { size: A4 landscape; margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; background: #f8fafc; }
      .cover { background: linear-gradient(135deg,#312e81,#0891b2,#16a34a); color: white; padding: 24px; border-radius: 18px; margin-bottom: 18px; }
      h1 { margin: 0; font-size: 28px; letter-spacing: -0.02em; }
      .sub { margin-top: 8px; font-size: 13px; opacity: 0.9; line-height: 1.5; }
      .meta { margin-top: 14px; display: flex; gap: 10px; flex-wrap: wrap; }
      .chip { background: rgba(255,255,255,0.18); border: 1px solid rgba(255,255,255,0.3); border-radius: 999px; padding: 7px 11px; font-weight: 700; font-size: 12px; }
      .report-section { background: white; border: 1px solid #dbeafe; border-radius: 16px; padding: 16px; margin-bottom: 16px; box-shadow: 0 10px 26px rgba(15,23,42,0.08); }
      h2 { margin: 0 0 6px; font-size: 18px; color: #1e293b; }
      h2 span { background: #4f46e5; color: white; border-radius: 999px; display: inline-flex; width: 26px; height: 26px; align-items: center; justify-content: center; font-size: 13px; }
      .count { color: #475569; font-size: 12px; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; overflow: hidden; border-radius: 12px; }
      th { background: #1e293b; color: white; font-size: 11px; text-align: left; padding: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
      td { border-bottom: 1px solid #e2e8f0; padding: 9px 10px; font-size: 12px; vertical-align: top; }
      tr:nth-child(even) td { background: #f1f5f9; }
      .empty { text-align: center; color: #64748b; padding: 24px; }
      .footer { margin-top: 16px; color: #64748b; font-size: 11px; text-align: center; }
      @media print { body { background: white; } .no-print { display: none; } .report-section { break-inside: avoid; } }
    </style>
  </head>
  <body>
    <div class="cover">
      <h1>${escapeHtml(title)}</h1>
      <div class="sub">${escapeHtml(subtitle)}</div>
      <div class="meta"><div class="chip">Farm OS</div><div class="chip">Generated: ${escapeHtml(createdOn)}</div></div>
    </div>
    ${sectionHtml}
    <div class="footer">Generated from AgriWealth Farm OS task report.</div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
  </body>
  </html>`;

  const win = window.open("", "_blank");
  if (!win) {
    toast.error("Please allow popups to export PDF.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

function SummaryCards({ items, type }) {
  if (!Array.isArray(items) || items.length === 0) return null;

  const cleanLines = (item) => {
    const lines = [];
    const matched = item.totalCandidates ?? item.totalAnimals ?? item.totalAssets;
    if (matched != null) lines.push(`${matched} animals matched this ${String(type || "task").toLowerCase()} rule`);
    if ((item.createdCount ?? 0) > 0) lines.push(`${item.createdCount} new task(s) created`);
    if ((item.alreadyExistsCount ?? 0) > 0) lines.push(`${item.alreadyExistsCount} task(s) already exist for this date`);
    if ((item.skippedNoRuleCount ?? 0) > 0) lines.push(`${item.skippedNoRuleCount} animal(s) had no matching rule`);
    if ((item.skippedNoWeightCount ?? 0) > 0) lines.push(`${item.skippedNoWeightCount} animal(s) skipped because weight was missing`);
    if ((item.skippedInvalidCount ?? 0) > 0) lines.push(`${item.skippedInvalidCount} invalid row(s) skipped`);
    return lines.length ? lines : ["No new tasks were created for this selection."];
  };

  return (
    <div style={styles.summaryGrid}>
      {items.map((item, idx) => (
        <div key={`${type}-${item.batchId || idx}`} style={styles.summaryCard}>
          <div style={styles.summaryTitle}>{item.batchName || `Batch ${item.batchId || "-"}`}</div>
          {cleanLines(item).map((line) => (
            <div key={line} style={styles.summaryLine}>{line}</div>
          ))}
        </div>
      ))}
    </div>
  );
}

function FeedingTaskTable({ tasks, onAction, selectedIds, onSelect, onSelectAll, quantities, onQuantityChange }) {
  const pending = tasks.filter((t) => t.status === 'PENDING');
  return (
    <div style={styles.tableWrap}>
      <table style={{ ...styles.table, minWidth: 1180, tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: 44 }} />
          <col style={{ width: 98 }} />
          <col style={{ width: 126 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 128 }} />
          <col style={{ width: 160 }} />
          <col style={{ width: 220 }} />
          <col style={{ width: 132 }} />
          <col style={{ width: 250 }} />
        </colgroup>
        <thead>
          <tr>
            <th style={styles.th}><input type="checkbox" aria-label="Select all pending feeding tasks"
              checked={pending.length > 0 && pending.every((t) => selectedIds.includes(t.id))}
              onChange={(e) => onSelectAll(e.target.checked, pending)} /></th>
            <th style={styles.th}>Due</th>
            <th style={styles.th}>Batch</th>
            <th style={styles.th}>Animal / Asset</th>
            <th style={styles.th}>Item</th>
            <th style={styles.th}>Planned</th>
            <th style={styles.th}>Actual</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td style={styles.emptyCell} colSpan={9}>
                No feeding tasks found.
              </td>
            </tr>
          ) : (
            tasks.map((t) => (
              <tr key={t.id}>
                <td style={styles.td}><input type="checkbox" aria-label={`Select feeding for ${t.assetId || t.id}`}
                  disabled={t.status !== 'PENDING'} checked={selectedIds.includes(t.id)}
                  onChange={(e) => onSelect(t.id, e.target.checked)} /></td>
                <td style={{ ...styles.td, ...styles.nowrapCell }}>{formatDate(t.dueDate)}</td>
                <td style={styles.td}><span style={styles.cellPrimary}>{t.batchName || "-"}</span></td>
                <td style={{ ...styles.td, ...styles.nowrapCell }}><span style={styles.cellPrimary}>{t.assetId || t.livestockId || "-"}</span></td>
                <td style={styles.td}>{t.components?.length ? t.components.map(c => <div key={c.id} style={styles.componentSummary}><span>{c.inventoryName}</span><strong>{c.percentage}%</strong></div>) : (t.itemName || "-")}</td>
                <td style={styles.td}>
                  <strong style={styles.plannedTotal}>{t.plannedTotalGrams != null ? `${t.plannedTotalGrams} g` : (t.plannedQuantity != null ? `${t.plannedQuantity} g` : "-")}</strong>
                  {t.basisWeightKg != null && <small style={styles.weightBasis}>{t.basisWeightKg} kg × {t.gramsPerKgPerDay} g/kg/day</small>}
                  <div style={styles.componentPlanList}>{t.components?.map(c => <div key={c.id} style={styles.componentPlanRow}><span>{c.inventoryName}</span><strong>{c.plannedGrams} g</strong></div>)}</div>
                </td>
                <td style={styles.td}>
                  {t.components?.length ? t.components.map(c => <div key={c.id} style={styles.actualComponentRow}>
                    <span style={styles.actualComponentName}>{c.inventoryName}</span>
                    {t.status === "PENDING" ? <input type="number" step="0.001" min="0.001" aria-label={`Actual ${c.inventoryName} grams for task ${t.id}`} style={styles.actualInput} value={quantities[`${t.id}:${c.id}`] ?? c.plannedGrams} onChange={e => onQuantityChange(`${t.id}:${c.id}`,e.target.value)} /> : <strong style={styles.actualValue}>{c.actualGrams ?? c.plannedGrams}</strong>}
                    <span style={styles.actualUnit}>g</span>
                  </div>) : (t.status === "PENDING" ? <div style={styles.singleActualRow}><input type="number" aria-label={`Actual grams for ${t.assetId || t.id}`} min="1" step="1" style={styles.actualInput} value={quantities[t.id] ?? t.plannedQuantity ?? ""} onChange={e=>onQuantityChange(t.id,e.target.value)} /><span style={styles.actualUnit}>g</span></div> : (t.actualQuantity != null ? `${t.actualQuantity} g` : "-"))}
                </td>
                <td style={{ ...styles.td, verticalAlign: "middle" }}>
                  <TaskStatusBadge status={t.status} />
                </td>
                <td style={{ ...styles.td, verticalAlign: "middle" }}>
                  {t.status === "PENDING" ? (
                    <div style={styles.actionStack}>
                      <button className="task-action-btn task-action-given" style={styles.actionBtnPrimary} onClick={() => onAction(t, "GIVEN")}>
                        Given
                      </button>
                      <button className="task-action-btn task-action-not-given" style={styles.actionBtnWarn} onClick={() => onAction(t, "NOT_GIVEN")}>
                        Not Given
                      </button>
                      <button className="task-action-btn task-action-skipped" style={styles.actionBtnMuted} onClick={() => onAction(t, "SKIPPED")}>
                        Skip
                      </button>
                      <button className="task-action-btn task-action-reschedule" style={styles.actionBtnSecondary} onClick={() => onAction(t, "RESCHEDULED")}>
                        Reschedule
                      </button>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}


// Aggregated batch-first feed entry. Individual records remain visible on expansion.
const FEED_SESSIONS = ["MORNING", "AFTERNOON", "EVENING", "NIGHT"];
const feedPlannedGrams = t => Number(t.plannedTotalGrams ?? t.plannedQuantity ?? 0);
const feedActualGrams = t => t.status === "GIVEN"
  ? Number(t.components?.length ? t.components.reduce((n,c)=>n+Number(c.actualGrams ?? 0),0) : t.actualQuantity ?? 0) : 0;
const rounded = n => Math.round(n * 1000) / 1000;

/** Allocate a batch's confirmed grams proportionally to its task and component weights. */
export function allocateBatchFeeding(tasks, totalKg) {
  const totalMilliGrams = Math.round(Number(totalKg) * 1000000);
  if (!Number.isFinite(totalMilliGrams) || totalMilliGrams <= 0 || !Number.isSafeInteger(totalMilliGrams) ||
      Math.abs(Number(totalKg)*1000-Math.round(Number(totalKg)*1000))>0.0000001) throw Error("Enter a positive batch quantity in kilograms (up to 3 decimals)");
  const entries = tasks.flatMap(t => (t.components || []).map(c => ({taskId:t.id, componentId:c.id, weight:Number(c.plannedGrams ?? 0)})));
  const totalWeight = entries.reduce((sum,e)=>sum+e.weight,0);
  if (!entries.length || !(totalWeight > 0) || entries.some(e=>!(e.weight>0))) throw Error("Cannot allocate a batch without positive planned component amounts");
  if (totalMilliGrams < entries.length) throw Error("Batch quantity too small for the component allocations");
  // Reserve one 0.001g for each component so a tiny share cannot become zero.
  const distributable = totalMilliGrams - entries.length;
  let allocated = 0;
  const values = entries.map((e,i)=>{
    const base = i===entries.length-1 ? distributable-allocated : Math.floor(distributable*e.weight/totalWeight);
    allocated += base;
    return {...e, actualGrams:(base+1)/1000};
  });
  return tasks.map(t=>({taskId:t.id,components:values.filter(v=>v.taskId===t.id).map(v=>({componentId:v.componentId,actualGrams:v.actualGrams}))}));
}

function BatchFeedingTasks({tasks, onAction, onBatchAction, onBatchGiven, loading, quantities, onQuantityChange, session, onSessionChange}) {
  const [opened,setOpened] = useState({});
  const [batchActual,setBatchActual] = useState({});
  const [manualAmounts,setManualAmounts] = useState({});
  const shown = tasks.filter(t => (t.feedingSession || "MORNING") === session);
  const groups = useMemo(() => {
    const map=new Map();
    shown.forEach(t=>{
      const key=String(t.batchId || t.batchName);
      if(!map.has(key))map.set(key,{key, batchName:t.batchName || `Batch ${t.batchId}`,rows:[]});
      map.get(key).rows.push(t);
    });
    return [...map.values()];
  },[shown]);
  const totalPlanned = groups.reduce((sum,g)=>sum+g.rows.reduce((s,t)=>s+feedPlannedGrams(t),0),0)/1000;
  const totalGiven = groups.reduce((sum,g)=>sum+g.rows.reduce((s,t)=>s+feedActualGrams(t),0),0)/1000;
  const chooseSession = value => { onSessionChange(value); setOpened({}); };
  return <div>
    <div style={{display:"flex",gap:16,flexWrap:"wrap",alignItems:"center",margin:"12px 0 20px"}}>
      <div style={{padding:14,border:"1px solid #cce7db",borderRadius:10,background:"#eaf7f0"}}>Total planned: <b>{totalPlanned.toFixed(3)} kg</b></div>
      <div style={{padding:14,border:"1px solid #cce7db",borderRadius:10,background:"#eaf7f0"}}>Total given: <b>{totalGiven.toFixed(3)} kg</b></div>
      <label style={{display:"flex",alignItems:"center",gap:8,marginLeft:"auto"}}>Load session
        <select aria-label="Feeding session" style={styles.input} value={session} onChange={e=>chooseSession(e.target.value)}>
          {[...new Set(tasks.map(t=>t.feedingSession || "MORNING"))].sort((a,b)=>FEED_SESSIONS.indexOf(a)-FEED_SESSIONS.indexOf(b)).map(name=><option key={name} value={name}>{name.charAt(0)+name.slice(1).toLowerCase()}</option>)}
        </select>
      </label>
    </div>
    <div style={styles.tableWrap}><table style={{...styles.table,minWidth:900}}><thead><tr>
      {['Batch','Livestock','Planned (KG)','Actual (KG)','Components','Status','Actions'].map(h=><th key={h} style={styles.th}>{h}</th>)}
    </tr></thead><tbody>
      {!groups.length && <tr><td colSpan={7} style={styles.emptyCell}>No feeding tasks for this session.</td></tr>}
      {groups.map(g=>{
        const pending=g.rows.filter(t=>t.status==='PENDING');
        const planned=g.rows.reduce((n,t)=>n+feedPlannedGrams(t),0)/1000;
        const recorded=g.rows.reduce((n,t)=>n+feedActualGrams(t),0)/1000;
        const draft=batchActual[`${session}:${g.key}`];
        const componentTotals=new Map();
        g.rows.forEach(t=>(t.components||[]).forEach(c=>componentTotals.set(c.inventoryName,(componentTotals.get(c.inventoryName)||0)+Number(c.plannedGrams||0))));
        const statuses=[...new Set(g.rows.map(t=>t.status))];
        const status= statuses.length===1 ? statuses[0] : 'PARTIAL';
        const expanded=!!opened[g.key];
        const batchKey=`${session}:${g.key}`;
        const customAmounts=manualAmounts[batchKey]||{};
        let previewQuantities={};
        let allocationError=null;
        if(pending.length) {
          try {
            const allocated=allocateBatchFeeding(pending,draft ?? (pending.reduce((n,t)=>n+feedPlannedGrams(t),0)/1000).toFixed(3));
            allocated.forEach(t=>t.components.forEach(c=>{previewQuantities[`${t.taskId}:${c.componentId}`]=c.actualGrams;}));
          } catch(error) { allocationError=error.message; }
        }
        previewQuantities={...previewQuantities,...customAmounts};
        const pendingComponents=pending.flatMap(t=>(t.components||[]).map(c=>({taskId:t.id,componentId:c.id,key:`${t.id}:${c.id}`})));
        const displayedGrams=pendingComponents.reduce((sum,c)=>sum+Number(previewQuantities[c.key]||0),0);
        const hasManualEdits=Object.keys(customAmounts).length>0;
        const batchDisplay=hasManualEdits?(displayedGrams/1000).toFixed(3):draft ?? (pending.length ? (pending.reduce((n,t)=>n+feedPlannedGrams(t),0)/1000).toFixed(3) : recorded.toFixed(3));
        const submitBatch=()=>{
          if(allocationError){toast.error(allocationError);return;}
          const items=pending.map(t=>({taskId:t.id,components:(t.components||[]).map(c=>({componentId:c.id,actualGrams:Number(previewQuantities[`${t.id}:${c.id}`])}))}));
          if(items.some(t=>!t.components.length||t.components.some(c=>!(c.actualGrams>0)||Math.abs(c.actualGrams*1000-Math.round(c.actualGrams*1000))>0.000001))){
            toast.error('Every medicine-independent feed component needs a positive actual amount with at most 3 decimal places.');return;
          }
          onBatchGiven(pending,(items.reduce((sum,t)=>sum+t.components.reduce((n,c)=>n+c.actualGrams,0),0)/1000).toFixed(6),g.batchName,items);
        };
        const updateAnimalAmount=(id,value)=>setManualAmounts(current=>({...current,[batchKey]:{...(current[batchKey]||{}),[id]:value}}));
        return <React.Fragment key={g.key}><tr>
          <td style={styles.td}><button type="button" aria-label={`${expanded?'Collapse':'Expand'} ${g.batchName}`} style={styles.ghostBtn} onClick={()=>setOpened(v=>({...v,[g.key]:!v[g.key]}))}>{expanded?'−':'+'}</button> <strong>{g.batchName}</strong></td>
          <td style={styles.td}>{g.rows.length}</td>
          <td style={styles.td}>{planned.toFixed(3)}</td>
          <td style={styles.td}><input aria-label={`Actual kg for ${g.batchName}`} type="number" min="0.001" step="0.001" style={{...styles.input,maxWidth:120}} value={batchDisplay} disabled={!pending.length || loading} onChange={e=>{setBatchActual(v=>({...v,[batchKey]:e.target.value}));setManualAmounts(v=>({...v,[batchKey]:{}}));}}/><small style={{display:'block'}}>Given: {recorded.toFixed(3)} kg</small></td>
          <td style={styles.td}>{[...componentTotals].map(([name,grams])=><div key={name}>{name}: {(grams/1000).toFixed(3)} kg</div>)}</td>
          <td style={styles.td}><TaskStatusBadge status={status}/></td>
          <td style={styles.td}><div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            <button type="button" className="task-action-btn task-action-given" disabled={!pending.length || loading} style={styles.actionBtnPrimary} onClick={submitBatch}>Given</button>
            {['NOT_GIVEN','SKIPPED','RESCHEDULED'].map(action=><button key={action} disabled={!pending.length || loading} type="button" style={styles.ghostBtn} onClick={()=>onBatchAction(pending,action,g.batchName)}>{action.replace('_',' ')}</button>)}
          </div></td>
        </tr>
        {expanded && <tr><td colSpan={7} style={{padding:12,background:'#f7faf8'}}><FeedingTaskTable tasks={g.rows}
             onAction={(task,status)=>{
               if(status==='GIVEN'&&task.status==='PENDING'){
                 const items=[{taskId:task.id,components:(task.components||[]).map(c=>({componentId:c.id,actualGrams:Number(previewQuantities[`${task.id}:${c.id}`])}))}];
                 const actual=items[0].components.reduce((sum,c)=>sum+c.actualGrams,0)/1000;
                 onBatchGiven([task],actual.toFixed(6),g.batchName,items);
               }else onAction(task,status);
             }}
             selectedIds={[]} onSelect={()=>{}} onSelectAll={()=>{}}
             quantities={previewQuantities} onQuantityChange={updateAnimalAmount}/></td></tr>}
        </React.Fragment>;
      })}
    </tbody></table></div>
  </div>;
}

function MedicineTaskTable({ tasks, onAction, selectedKeys, onSelect, onSelectAll }) {
  const pending = tasks.filter((task) => task.status === "PENDING");
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}><input type="checkbox" aria-label="Select all pending medicine tasks" checked={pending.length>0&&pending.every(t=>selectedKeys.includes(t.combinationKey))} onChange={e=>onSelectAll(e.target.checked,pending)}/></th>
            <th style={styles.th}>Due</th>
            <th style={styles.th}>Batch</th>
            <th style={styles.th}>Asset</th>
            <th style={styles.th}>Medicine</th>
            <th style={styles.th}>Planned</th>
            <th style={styles.th}>Route</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Notes</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td style={styles.emptyCell} colSpan={10}>
                No medicine tasks found.
              </td>
            </tr>
          ) : (
            tasks.map((t) => (
              <tr key={t.combinationKey}>
                <td style={styles.td}><input type="checkbox" disabled={t.status!=="PENDING"} checked={selectedKeys.includes(t.combinationKey)} onChange={e=>onSelect(t.combinationKey,e.target.checked)}/></td>
                <td style={styles.td}>{formatDate(t.dueDate)}</td>
                <td style={styles.td}>{t.batchName || "-"}</td>
                <td style={styles.td}>{t.assetId || "-"}</td>
                <td style={styles.td}><b>{t.combinationName}</b></td>
                <td style={styles.td}>
                  {t.components.map(c=>c.plannedQuantityDecimal != null || c.plannedQuantity != null ? `${c.itemName||c.medicineName||"Medicine"}: ${c.plannedQuantityDecimal ?? c.plannedQuantity} ${c.unit||""}`.trim():null).filter(Boolean).join(" + ")||"-"}
                </td>
                <td style={styles.td}>{[...new Set(t.components.map(c=>c.medicineRoute||c.route).filter(Boolean))].join(" + ")||"-"}</td>
                <td style={styles.td}>
                  <TaskStatusBadge status={t.status} />
                </td>
                <td style={styles.td}>{t.notes || "-"}</td>
                <td style={styles.td}>
                  {t.status === "PENDING" ? (
                    <div style={styles.actionStack}>
                      <button className="task-action-btn task-action-given" style={styles.actionBtnPrimary} onClick={() => onAction(t, "GIVEN")}>
                        {t.combinationName} Given
                      </button>
                      <button className="task-action-btn task-action-not-given" style={styles.actionBtnWarn} onClick={() => onAction(t, "NOT_GIVEN")}>
                        Not Given
                      </button>
                      <button className="task-action-btn task-action-skipped" style={styles.actionBtnMuted} onClick={() => onAction(t, "SKIPPED")}>
                        Skip
                      </button>
                      <button className="task-action-btn task-action-reschedule" style={styles.actionBtnSecondary} onClick={() => onAction(t, "RESCHEDULED")}>
                        Reschedule
                      </button>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function MedicineHistoryTable({ rows }) {
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Batch</th>
            <th style={styles.th}>Asset</th>
            <th style={styles.th}>Medicine</th>
            <th style={styles.th}>Route</th>
            <th style={styles.th}>Given Today</th>
            <th style={styles.th}>Condition</th>
            <th style={styles.th}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={styles.emptyCell} colSpan={8}>
                No medicine history found.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td style={styles.td}>{formatDate(r.logDate)}</td>
                <td style={styles.td}>{r.batchName || r.batchId || "-"}</td>
                <td style={styles.td}>{r.assetId || "-"}</td>
                <td style={styles.td}>{r.medicineName || "-"}</td>
                <td style={styles.td}>{r.route || "-"}</td>
                <td style={styles.td}>{r.medicineGivenToday ? "Yes" : "No"}</td>
                <td style={styles.td}>{r.conditionToday || "-"}</td>
                <td style={styles.td}>{r.notes || "-"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function TaskPage() {
  const [tab, setTab] = useState("vaccine");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [batches, setBatches] = useState([]);
  const [reportMode, setReportMode] = useState("selected");
  const [reportDate, setReportDate] = useState(todayStr());
  const [reportBusy, setReportBusy] = useState(false);

  const [vaccineFilters, setVaccineFilters] = useState({
    species: "GOAT",
    projectType: "FATTENING",
    date: todayStr(),
    status: "",
  });
  const [vaccineTasks, setVaccineTasks] = useState([]);
  const [selectedVaccineKeys, setSelectedVaccineKeys] = useState([]);

  const [feedingFilters, setFeedingFilters] = useState({
    species: "GOAT",
    projectType: "FATTENING",
    date: todayStr(),
    batchId: "",
    status: "",
  });
  const [feedingTasks, setFeedingTasks] = useState([]);
  const [feedingSession,setFeedingSession] = useState("MORNING");
  const [selectedFeedingIds, setSelectedFeedingIds] = useState([]);
  const [feedingActuals, setFeedingActuals] = useState({});

  const [medicineFilters, setMedicineFilters] = useState({
    batchId: "",
    date: todayStr(),
    status: "PENDING",
    assetId: "",
    from: todayStr(),
    to: todayStr(),
  });
  const [medicineSummary, setMedicineSummary] = useState([]);
  const [medicineTasks, setMedicineTasks] = useState([]);
  const [selectedMedicineKeys, setSelectedMedicineKeys] = useState([]);
  const [medicineHistory, setMedicineHistory] = useState([]);

  const batchOptions = useMemo(() => (Array.isArray(batches) ? batches : []), [batches]);
  const combinedVaccineTasks = useMemo(() => combineTaskRows(vaccineTasks, "vaccine"), [vaccineTasks]);
  const combinedMedicineTasks = useMemo(() => combineTaskRows(medicineTasks, "medicine"), [medicineTasks]);

  useEffect(() => {
    (async () => {
      try {
        const data = await listBatchesIdName();
        setBatches(data || []);
      } catch (e) {
        setError(errMsg(e));
      }
    })();
  }, []);

  async function loadVaccineTasks() {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await taskService.listVaccineTasksByProjectType(vaccineFilters);
      setVaccineTasks(data || []);
      setSelectedVaccineKeys([]);
      setMessage(`Loaded ${combineTaskRows(data || [], "vaccine").length} combined vaccine task(s).`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function markVaccineComplete(task) {
    try {
      if (!window.confirm(`${task.combinationName} given to ${animalIdentity(task)}?`)) return;
      setLoading(true);
      setError("");
      setMessage("");
      if(task.taskIds.length>1)await taskService.markVaccineTasksGiven(task.taskIds);
      else await taskService.completeVaccineTask(task.taskIds[0]);
      setMessage(`${task.combinationName} marked as Given.`);
      await loadVaccineTasks();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function markVaccineNotGiven(task) {
    if (!window.confirm(`${task.combinationName} was not given to ${animalIdentity(task)}?`)) return;
    try{setLoading(true);setError("");
      if(task.taskIds.length>1)await taskService.skipVaccineTasks(task.taskIds);
      else await taskService.skipVaccineTask(task.taskIds[0]);
      setMessage(`${task.combinationName} marked as Not Given.`);await loadVaccineTasks();
    }catch(e){setError(errMsg(e));}finally{setLoading(false);}
  }

  async function completeSelectedVaccines() {
    const chosen=combinedVaccineTasks.filter(t=>selectedVaccineKeys.includes(t.combinationKey)&&["PENDING","TODAY","MISSED"].includes(t.status));
    if(!chosen.length)return;
    if(!window.confirm(`Mark ${chosen.length} selected animal vaccine task(s) as Given / Done?`))return;
    try{setLoading(true);setError("");
      for(const group of chosen){
        if(group.taskIds.length>1)await taskService.markVaccineTasksGiven(group.taskIds);
        else await taskService.completeVaccineTask(group.taskIds[0]);
      }
      setMessage(`${chosen.length} combined vaccine task(s) completed.`);await loadVaccineTasks();
    }catch(e){setError(errMsg(e));}finally{setLoading(false);}
  }

  async function loadFeedingTasks(showMessage = true, autoAdvance = false) {
    try {
      setLoading(true);
      setError("");
      if (showMessage) setMessage("");
      // Recover missing daily tasks when cron was offline or the server restarted.
      // Do not generate future tasks before their due day.
      let generationSummary = '';
      if (feedingFilters.date && feedingFilters.date <= todayStr()) {
        try {
          const generated = await taskService.syncFeedingTasks(feedingFilters.date);
          const totals = (generated || []).reduce((a,r) => ({created:a.created+(r.createdCount||0), missing:a.missing+(r.skippedNoWeightCount||0)}), {created:0, missing:0});
          generationSummary = `${totals.created} created; ${totals.missing} need a weight log.`;
        } catch (syncError) {
          generationSummary = `Generation warning: ${errMsg(syncError)}`;
        }
      }
      const data = await taskService.listFeedingTasks({
        batchId: feedingFilters.batchId || undefined,
        species: feedingFilters.batchId ? undefined : feedingFilters.species,
        projectType: feedingFilters.batchId ? undefined : feedingFilters.projectType,
        date: feedingFilters.date,
        status: feedingFilters.status || undefined,
      });
      setFeedingTasks(data || []);
      setSelectedFeedingIds([]);
      // Start with the first session that still has outstanding animals.
      const list = data || [];
      const available = [...new Set(list.map(t => t.feedingSession || "MORNING"))].sort((a,b)=>FEED_SESSIONS.indexOf(a)-FEED_SESSIONS.indexOf(b));
      setFeedingSession(prev => !autoAdvance && available.includes(prev) ? prev : (available.find(name=>list.some(t=>(t.feedingSession||"MORNING")===name&&t.status==='PENDING')) || available[0] || 'MORNING'));
      if (showMessage) setMessage(`Loaded ${data?.length || 0} feeding task(s). ${generationSummary}`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function feedingDecision(task, status) {
    try {
      let payload = { status, notes: "" };

      if (status === "GIVEN") {
        const edits = (task.components || []).map(c => ({componentId: c.id, actualGrams: Number(feedingActuals[`${task.id}:${c.id}`] ?? c.plannedGrams)}));
        if(edits.some(c => !Number.isFinite(c.actualGrams) || c.actualGrams <= 0 || Math.round(c.actualGrams * 1000) !== c.actualGrams * 1000)) {
          setError("Enter a valid positive actual amount (up to 3 decimals) for every feed component."); return;
        }
        if(!window.confirm(`Mark complete ration for task #${task.id} Given? All component stock and costs will be posted together.`)) return;
        setLoading(true);setError("");setMessage("");
        await taskService.completeFeedingTasksBulk([{taskId:task.id,components:edits}]);
        setMessage(`Complete ration for task #${task.id} recorded.`);
        await loadFeedingTasks(false);
        return;
      } else if (status === "RESCHEDULED") {
        const newDate = window.prompt("Enter new date (YYYY-MM-DD)", todayStr());
        if (!newDate) return;
        payload.rescheduledToDate = newDate;
        payload.notes = window.prompt("Reason / notes", "") || "";
      } else {
        payload.notes = window.prompt("Notes (optional)", "") || "";
      }

      setLoading(true);
      setError("");
      setMessage("");
      await taskService.decideFeedingTask(task.id, payload);
      setMessage(`Feeding task #${task.id} updated as ${status}.`);
      await loadFeedingTasks(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  const selectFeedingTask = (id, selected) => setSelectedFeedingIds((prev) =>
    selected ? [...new Set([...prev, id])] : prev.filter((x) => x !== id));

  const selectAllFeedingTasks = (selected, pending) => setSelectedFeedingIds((prev) =>
    selected ? [...new Set([...prev, ...pending.map((t) => t.id)])] : prev.filter((id) => !pending.some((t) => t.id === id)));

  async function completeSelectedFeeding() {
    const chosen = feedingTasks.filter((t) => selectedFeedingIds.includes(t.id) && t.status === 'PENDING');
    if (!chosen.length) return;
    const items = chosen.map(t => ({
      taskId:t.id,
      components:(t.components || []).map(c => ({componentId:c.id,actualGrams:Number(feedingActuals[`${t.id}:${c.id}`] ?? c.plannedGrams)})),
    }));
    if(items.some(item => item.components.some(c => !Number.isFinite(c.actualGrams) || c.actualGrams <= 0 || Math.round(c.actualGrams*1000) !== c.actualGrams*1000))) {
      setError("Every component must have a positive actual quantity with up to 3 decimals.");return;
    }
    if (!window.confirm(`Mark ${items.length} feeding task(s) Given? Actual amounts will be deducted from inventory once.`)) return;
    setLoading(true);
    setError('');
    try {
      await taskService.completeFeedingTasksBulk(items);
      await loadFeedingTasks(false);
      setMessage(`${items.length} feeding task(s) completed; inventory updated.`);
    } catch (e) {
      setError(`Bulk completion failed (no partial stock deductions): ${errMsg(e)}`);
    } finally { setLoading(false); }
  }

  async function decideBatchFeeding(pending,status,batchName){
    if(!pending.length) return;
    let rescheduledToDate=null;
    if(status==='RESCHEDULED'){
      rescheduledToDate=window.prompt('Reschedule the batch to (YYYY-MM-DD)',todayStr());
      if(!rescheduledToDate)return;
    }
    const notes=window.prompt(`Reason / notes for ${status} (${batchName})`, '') ?? null;
    if(notes===null)return;
    if(!window.confirm(`Apply ${status} to ${pending.length} feeding tasks in ${batchName}?`))return;
    try{
      setLoading(true);setError('');
      await taskService.decideFeedingTasksBulk(pending.map(t=>t.id),{status,rescheduledToDate,notes});
      await loadFeedingTasks(false,true);
      setMessage(`Batch ${batchName}: ${pending.length} tasks marked ${status}.`);
    }catch(e){setError(errMsg(e));}finally{setLoading(false);}
  }

  async function completeBatchFeeding(pending, actualKg, batchName, itemOverrides) {
    if (!pending.length) return;
    try {
      const items=itemOverrides || allocateBatchFeeding(pending,actualKg);
      if(items.length!==pending.length || items.some(t=>!t.components.length||t.components.some(c=>!(Number(c.actualGrams)>0)||Math.abs(Number(c.actualGrams)*1000-Math.round(Number(c.actualGrams)*1000))>0.000001)))
        throw new Error('Enter valid positive component quantities (up to 3 decimals) before confirming.');
      if(!window.confirm(`Give ${Number(actualKg).toFixed(3)} kg in ${batchName} to ${pending.length} livestock records? Inventory will be deducted.`))return;
      setLoading(true);setError("");
      await taskService.completeFeedingTasksBulk(items);
      const data=await taskService.listFeedingTasks({
        batchId:feedingFilters.batchId||undefined,
        species:feedingFilters.batchId?undefined:feedingFilters.species,
        projectType:feedingFilters.batchId?undefined:feedingFilters.projectType,
        date:feedingFilters.date,
        status:feedingFilters.status||undefined,
      });
      setFeedingTasks(data||[]);
      const remaining=(data||[]).filter(t=>(t.feedingSession||'MORNING')===feedingSession&&t.status==='PENDING');
      if(!remaining.length){
        const next=FEED_SESSIONS.slice(FEED_SESSIONS.indexOf(feedingSession)+1)
          .find(name=>(data||[]).some(t=>t.feedingSession===name&&t.status==='PENDING'));
        if(next)setFeedingSession(next);
      }
      setMessage(`Batch ${batchName} updated: ${pending.length} feeding tasks completed and inventory deducted.`);
    } catch(e){setError(errMsg(e));}
    finally{setLoading(false);}
  }

  async function previewMedicine() {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await taskService.previewMedicineGeneration({
        batchId: medicineFilters.batchId || undefined,
        date: medicineFilters.date,
      });
      setMedicineSummary(data || []);
      setMessage(`Preview generated for ${data?.length || 0} batch(es).`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function generateMedicine() {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await taskService.generateMedicineTasks({
        batchId: medicineFilters.batchId || undefined,
        date: medicineFilters.date,
      });
      setMedicineSummary(data || []);
      setMessage(`Pending medicine tasks generated for ${data?.length || 0} batch(es).`);
      await loadMedicineTasks(false);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadMedicineTasks(showMessage = true) {
    try {
      setLoading(true);
      setError("");
      if (showMessage) setMessage("");
      const data = await taskService.listMedicineTasks({
        batchId: medicineFilters.batchId || undefined,
        date: medicineFilters.date,
        status: medicineFilters.status || undefined,
      });
      setMedicineTasks(data || []);
      setSelectedMedicineKeys([]);
      if (showMessage) setMessage(`Loaded ${combineTaskRows(data || [], "medicine").length} combined medicine task(s).`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadMedicineHistory() {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await taskService.listMedicineLogs({
        batchId: medicineFilters.batchId || undefined,
        assetId: medicineFilters.assetId || undefined,
        from: medicineFilters.from || medicineFilters.date,
        to: medicineFilters.to || medicineFilters.date,
      });
      setMedicineHistory(data || []);
      setMessage(`Loaded ${data?.length || 0} medicine history row(s).`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function medicineDecision(task, status) {
    try {
      let payload = { status, notes: "" };

      if (status === "GIVEN") {
        if (!window.confirm(`${task.combinationName} given to ${animalIdentity(task)}?`)) return;
        const conditionToday = window.prompt(
          `Condition today (${CONDITION_OPTIONS.join(" / ")})`,
          "SAME"
        );
        payload.conditionToday = (conditionToday || "SAME").toUpperCase();
        payload.actionDate = todayStr();
        payload.notes = window.prompt("Notes (optional)", "") || "";
      } else if (status === "RESCHEDULED") {
        const newDate = window.prompt("Enter new date (YYYY-MM-DD)", todayStr());
        if (!newDate) return;
        payload.rescheduledToDate = newDate;
        payload.notes = window.prompt("Reason / notes", "") || "";
      } else {
        payload.notes = window.prompt("Notes (optional)", "") || "";
      }

      setLoading(true);
      setError("");
      setMessage("");
      for (const component of task.components) await taskService.decideMedicineTask(component.id, {
        ...payload,
        actualQuantity: component.plannedQuantityDecimal ?? component.plannedQuantity,
      });
      setMessage(`${task.combinationName} updated as ${status}.`);
      await loadMedicineTasks(false);
      await loadMedicineHistory();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function completeSelectedMedicines() {
    const chosen=combinedMedicineTasks.filter(t=>selectedMedicineKeys.includes(t.combinationKey)&&t.status==="PENDING");
    if(!chosen.length)return;
    if(!window.confirm(`Mark ${chosen.length} selected animal medicine task(s) as Given / Done? All medicines in each combination will be completed together.`))return;
    try{setLoading(true);setError("");
      for(const group of chosen)for(const component of group.components)await taskService.decideMedicineTask(component.id,{status:"GIVEN",actualQuantity:component.plannedQuantityDecimal??component.plannedQuantity,conditionToday:"SAME",actionDate:todayStr(),notes:""});
      setMessage(`${chosen.length} combined medicine task(s) completed.`);await loadMedicineTasks(false);
    }catch(e){setError(errMsg(e));}finally{setLoading(false);}
  }


  function apiDateForReport() {
    if (reportMode === "today") return todayStr();
    if (["selected", "pastDate", "futureDate"].includes(reportMode)) return reportDate || todayStr();
    return undefined;
  }

  function apiRangeForReport() {
    if (reportMode === "allPast") return { to: todayStr() };
    if (reportMode === "allFuture") return { from: todayStr() };
    return {};
  }

  async function fetchReportRows(type) {
    const date = apiDateForReport();
    const range = apiRangeForReport();
    let rows = [];
    if (type === "vaccine") {
      rows = await taskService.listVaccineTasksByProjectType({
        species: vaccineFilters.species,
        projectType: vaccineFilters.projectType,
        date,
        ...range,
        status: undefined,
      });
    } else if (type === "feeding") {
      rows = await taskService.listFeedingTasks({
        batchId: feedingFilters.batchId || undefined,
        species: feedingFilters.batchId ? undefined : feedingFilters.species,
        projectType: feedingFilters.batchId ? undefined : feedingFilters.projectType,
        date,
        ...range,
        status: undefined,
      });
    } else {
      rows = await taskService.listMedicineTasks({
        batchId: medicineFilters.batchId || undefined,
        date,
        ...range,
        status: undefined,
      });
    }
    return filterRowsByReportMode(rows || [], type, reportMode, reportDate);
  }

  async function downloadTaskReport(type = tab) {
    try {
      setReportBusy(true);
      setError("");
      const rows = await fetchReportRows(type);
      const modeLabel = REPORT_MODES.find((m) => m.value === reportMode)?.label || reportMode;
      openPrintableReport({
        title: titleForType(type),
        subtitle: `Mode: ${modeLabel}${apiDateForReport() ? ` • Date: ${apiDateForReport()}` : ""}`,
        sections: [{ title: titleForType(type), type, rows, batches: batchOptions }],
      });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setReportBusy(false);
    }
  }

  async function downloadAllTaskReports() {
    try {
      setReportBusy(true);
      setError("");
      const types = ["vaccine", "feeding", "medicine"];
      const sections = [];
      for (const type of types) {
        const rows = await fetchReportRows(type);
        sections.push({ title: titleForType(type), type, rows, batches: batchOptions });
      }
      const modeLabel = REPORT_MODES.find((m) => m.value === reportMode)?.label || reportMode;
      openPrintableReport({
        title: "Farm Task Report - Vaccine, Food and Medicine",
        subtitle: `Mode: ${modeLabel}${apiDateForReport() ? ` • Date: ${apiDateForReport()}` : ""}`,
        sections,
      });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setReportBusy(false);
    }
  }

  return (
    <div className="farm-task-page" style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h2 style={styles.title}>Tasks</h2>
          <p style={styles.subtitle}>
Vaccine tasks are generated from vaccine schedules. Feeding tasks are created automatically from active feeding rules; use this page to load and mark tasks as Given / Skip / Reschedule.
          </p>
        </div>
      </div>

      <div style={styles.tabRow}>
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`task-tab-btn${tab === item.key ? " is-active" : ""}`}
            onClick={() => {
              setTab(item.key);
              setMessage("");
              setError("");
            }}
            style={{
              ...styles.tab,
              ...(tab === item.key ? styles.tabActive : {}),
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {message ? <div style={styles.successBox}>{message}</div> : null}
      {error ? <div style={styles.errorBox}>{error}</div> : null}

      <section style={styles.reportCard}>
        <div>
          <h3 style={styles.reportTitle}>Task Report PDF</h3>
          <p style={styles.reportSubtitle}>Select date range mode and download colourful PDF reports for Vaccine / Food / Medicine tasks.</p>
        </div>
        <div style={styles.reportControls}>
          <label style={styles.field}>
            <span style={styles.label}>Report Mode</span>
            <select style={styles.input} value={reportMode} onChange={(e) => setReportMode(e.target.value)}>
              {REPORT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          {!["all", "allPast", "allFuture", "today"].includes(reportMode) ? (
            <label style={styles.field}>
              <span style={styles.label}>Selected Date</span>
              <input style={styles.input} type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
            </label>
          ) : null}
          <button className="task-control-btn task-control-primary" style={styles.primaryBtn} onClick={() => downloadTaskReport(tab)} disabled={reportBusy}>
            {reportBusy ? "Preparing..." : `Download ${TABS.find((x) => x.key === tab)?.label || "Task"} PDF`}
          </button>
          <button className="task-control-btn task-control-secondary" style={styles.secondaryBtn} onClick={downloadAllTaskReports} disabled={reportBusy}>
            Download All PDFs Data
          </button>
        </div>
      </section>

      {tab === "vaccine" && (
        <section style={styles.card}>
          <h3 style={styles.sectionTitle}>Vaccine Tasks by Project Type</h3>

          <div style={styles.infoBox}>
            Vaccine tasks are created automatically when a vaccine schedule is saved. Repeat reminders become pending on their due date.
          </div>

          <div style={styles.grid4}>
            <label style={styles.field}>
              <span style={styles.label}>Species</span>
              <select
                style={styles.input}
                value={vaccineFilters.species}
                onChange={(e) => setVaccineFilters((s) => ({ ...s, species: e.target.value }))}
              >
                {SPECIES_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Project Type</span>
              <select
                style={styles.input}
                value={vaccineFilters.projectType}
                onChange={(e) => setVaccineFilters((s) => ({ ...s, projectType: e.target.value }))}
              >
                {PROJECT_TYPE_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Date</span>
              <input
                style={styles.input}
                type="date"
                value={vaccineFilters.date}
                onChange={(e) => setVaccineFilters((s) => ({ ...s, date: e.target.value }))}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Status</span>
              <select
                style={styles.input}
                value={vaccineFilters.status}
                onChange={(e) => setVaccineFilters((s) => ({ ...s, status: e.target.value }))}
              >
                {VACCINE_STATUS_OPTIONS.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item || "All"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={styles.buttonRow}>
            <button className="task-control-btn task-control-primary" style={styles.primaryBtn} onClick={loadVaccineTasks} disabled={loading}>
              {loading ? "Loading..." : "Load Vaccine Tasks"}
            </button>
            <button className="task-control-btn task-control-primary" style={styles.primaryBtn} onClick={completeSelectedVaccines} disabled={loading||!selectedVaccineKeys.length}>
              Mark Selected as Given / Done
            </button>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}><input type="checkbox" aria-label="Select all pending vaccine tasks" checked={combinedVaccineTasks.some(t=>["PENDING","TODAY","MISSED"].includes(t.status))&&combinedVaccineTasks.filter(t=>["PENDING","TODAY","MISSED"].includes(t.status)).every(t=>selectedVaccineKeys.includes(t.combinationKey))} onChange={e=>setSelectedVaccineKeys(e.target.checked?combinedVaccineTasks.filter(t=>["PENDING","TODAY","MISSED"].includes(t.status)).map(t=>t.combinationKey):[])}/></th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Batch</th>
                  <th style={styles.th}>Animal</th>
                  <th style={styles.th}>Vaccine</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vaccineTasks.length === 0 ? (
                  <tr>
                    <td style={styles.emptyCell} colSpan={7}>
                      No vaccine tasks loaded.
                    </td>
                  </tr>
                ) : (
                  combinedVaccineTasks.map((task) => (
                    <tr key={task.combinationKey}>
                      <td style={styles.td}><input type="checkbox" disabled={!['PENDING','TODAY','MISSED'].includes(task.status)} checked={selectedVaccineKeys.includes(task.combinationKey)} onChange={e=>setSelectedVaccineKeys(prev=>e.target.checked?[...new Set([...prev,task.combinationKey])]:prev.filter(k=>k!==task.combinationKey))}/></td>
                      <td style={styles.td}>{formatDate(task.dueDate || task.taskDate || task.date)}</td>
                      <td style={styles.td}>{task.batchName || getBatchName(task.batchId, batchOptions)}</td>
                      <td style={styles.td}>{task.animalName || task.animalTag || task.animalId || task.assetId || task.livestockId || "All"}</td>
                      <td style={styles.td}><b>{task.combinationName}</b></td>
                      <td style={styles.td}>
                        <TaskStatusBadge status={task.status} />
                      </td>
                      <td style={styles.td}>
                        {["PENDING", "TODAY", "MISSED"].includes(task.status) ? (
                          <div style={styles.actionStack}><button
                            className="task-action-btn task-action-given"
                            style={styles.actionBtnPrimary}
                            onClick={() => markVaccineComplete(task)}
                            disabled={loading}
                          >
                            Given
                          </button><button className="task-action-btn task-action-not-given" style={styles.actionBtnWarn} onClick={()=>markVaccineNotGiven(task)} disabled={loading}>Not Given</button></div>
                        ) : task.status === "UPCOMING" ? (
                          <span style={{ color: "#94a3b8" }}>Upcoming</span>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "feeding" && (
        <section style={styles.card}>
          <h3 style={styles.sectionTitle}>Food / Feeding Tasks</h3>

          <div style={styles.infoBox}>
            Feeding tasks are now created automatically by the backend cron job from active feeding rules and their start date. Inventory reduces only when you mark a task as <b>Given</b>.
          </div>

          <div style={styles.grid5}>
            <label style={styles.field}>
              <span style={styles.label}>Species</span>
              <select
                style={styles.input}
                value={feedingFilters.species}
                onChange={(e) => setFeedingFilters((s) => ({ ...s, species: e.target.value }))}
              >
                {SPECIES_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Project Type</span>
              <select
                style={styles.input}
                value={feedingFilters.projectType}
                onChange={(e) => setFeedingFilters((s) => ({ ...s, projectType: e.target.value }))}
              >
                {PROJECT_TYPE_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Date</span>
              <input
                style={styles.input}
                type="date"
                value={feedingFilters.date}
                onChange={(e) => setFeedingFilters((s) => ({ ...s, date: e.target.value }))}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Batch (optional)</span>
              <select
                style={styles.input}
                value={feedingFilters.batchId}
                onChange={(e) => setFeedingFilters((s) => ({ ...s, batchId: e.target.value }))}
              >
                <option value="">All matching batches</option>
                {batchOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batchName || b.name || `Batch ${b.id}`}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Status</span>
              <select
                style={styles.input}
                value={feedingFilters.status}
                onChange={(e) => setFeedingFilters((s) => ({ ...s, status: e.target.value }))}
              >
                {TASK_STATUS_OPTIONS.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item || "All"}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={styles.buttonRow}>
            <button className="task-control-btn task-control-ghost" style={styles.ghostBtn} onClick={() => loadFeedingTasks()} disabled={loading}>
              Load Tasks
            </button>
          </div>

          <div style={{...styles.buttonRow, alignItems:'center', flexWrap:'wrap'}}>
            <b>{selectedFeedingIds.length} pending task(s) selected</b>
            <button className="task-control-btn task-control-primary" style={styles.primaryBtn} disabled={loading || !selectedFeedingIds.length}
              onClick={completeSelectedFeeding}>Mark Selected as Given / Done</button>
          </div>
          <h4 style={styles.subsectionTitle}>Pending / Final Feeding Tasks</h4>
          <BatchFeedingTasks tasks={feedingTasks} onAction={feedingDecision} onBatchGiven={completeBatchFeeding} onBatchAction={decideBatchFeeding}
            session={feedingSession} onSessionChange={setFeedingSession} loading={loading}
            quantities={feedingActuals} onQuantityChange={(id,value) => setFeedingActuals((prev) => ({...prev,[id]:value}))} />
        </section>
      )}

      {tab === "medicine" && (
        <section style={styles.card}>
          <h3 style={styles.sectionTitle}>Medicine Tasks</h3>

          <div style={styles.infoBox}>
            Medicine tasks are generated from medicine plans. Mark each task manually after actual administration.
          </div>

          <div style={styles.grid5}>
            <label style={styles.field}>
              <span style={styles.label}>Batch</span>
              <select
                style={styles.input}
                value={medicineFilters.batchId}
                onChange={(e) => setMedicineFilters((s) => ({ ...s, batchId: e.target.value }))}
              >
                <option value="">All batches</option>
                {batchOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batchName || b.name || `Batch ${b.id}`}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Date</span>
              <input
                style={styles.input}
                type="date"
                value={medicineFilters.date}
                onChange={(e) =>
                  setMedicineFilters((s) => ({
                    ...s,
                    date: e.target.value,
                    from: e.target.value,
                    to: e.target.value,
                  }))
                }
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Status</span>
              <select
                style={styles.input}
                value={medicineFilters.status}
                onChange={(e) => setMedicineFilters((s) => ({ ...s, status: e.target.value }))}
              >
                {TASK_STATUS_OPTIONS.map((item) => (
                  <option key={item || "all"} value={item}>
                    {item || "All"}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Asset (history filter)</span>
              <input
                style={styles.input}
                value={medicineFilters.assetId}
                onChange={(e) => setMedicineFilters((s) => ({ ...s, assetId: e.target.value }))}
                placeholder="Optional asset id"
              />
            </label>

            <div style={{ display: "grid", gap: 8 }}>
              <label style={styles.field}>
                <span style={styles.label}>History From</span>
                <input
                  style={styles.input}
                  type="date"
                  value={medicineFilters.from}
                  onChange={(e) => setMedicineFilters((s) => ({ ...s, from: e.target.value }))}
                />
              </label>
              <label style={styles.field}>
                <span style={styles.label}>History To</span>
                <input
                  style={styles.input}
                  type="date"
                  value={medicineFilters.to}
                  onChange={(e) => setMedicineFilters((s) => ({ ...s, to: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <div style={styles.buttonRow}>
            <button className="task-control-btn task-control-secondary" style={styles.secondaryBtn} onClick={previewMedicine} disabled={loading}>
              Preview Generation
            </button>
            <button className="task-control-btn task-control-primary" style={styles.primaryBtn} onClick={generateMedicine} disabled={loading}>
              Generate Pending Tasks
            </button>
            <button className="task-control-btn task-control-ghost" style={styles.ghostBtn} onClick={() => loadMedicineTasks()} disabled={loading}>
              Load Tasks
            </button>
            <button className="task-control-btn task-control-ghost" style={styles.ghostBtn} onClick={loadMedicineHistory} disabled={loading}>
              Load History
            </button>
          </div>

          <SummaryCards items={medicineSummary} type="MEDICINE" />

          <h4 style={styles.subsectionTitle}>Pending / Final Medicine Tasks</h4>
          <div style={{...styles.buttonRow,alignItems:"center",flexWrap:"wrap"}}>
            <b>{selectedMedicineKeys.length} animal task(s) selected</b>
            <button className="task-control-btn task-control-primary" style={styles.primaryBtn} disabled={loading||!selectedMedicineKeys.length} onClick={completeSelectedMedicines}>Mark Selected as Given / Done</button>
          </div>
          <MedicineTaskTable tasks={combinedMedicineTasks} onAction={medicineDecision} selectedKeys={selectedMedicineKeys}
            onSelect={(key,selected)=>setSelectedMedicineKeys(prev=>selected?[...new Set([...prev,key])]:prev.filter(k=>k!==key))}
            onSelectAll={(selected,pending)=>setSelectedMedicineKeys(selected?pending.map(t=>t.combinationKey):[])} />

          <h4 style={styles.subsectionTitle}>Legacy Medicine History</h4>
          <MedicineHistoryTable rows={medicineHistory} />
        </section>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: 24,
    color: "white",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 800,
  },
  subtitle: {
    margin: "8px 0 0",
    color: "#94a3b8",
    maxWidth: 900,
    lineHeight: 1.5,
  },
  tabRow: {
    display: "flex",
    gap: 12,
    marginBottom: 18,
    flexWrap: "wrap",
  },
  tab: {
    background: "#1e293b",
    color: "#cbd5e1",
    border: "1px solid #334155",
    borderRadius: 12,
    padding: "12px 18px",
    cursor: "pointer",
    fontWeight: 700,
  },
  tabActive: {
    background: "#4f46e5",
    color: "white",
    borderColor: "#6366f1",
    boxShadow: "0 0 0 2px rgba(99,102,241,0.25)",
  },
  successBox: {
    marginBottom: 16,
    background: "rgba(22,163,74,0.12)",
    border: "1px solid rgba(22,163,74,0.35)",
    color: "#bbf7d0",
    padding: 14,
    borderRadius: 12,
  },
  errorBox: {
    marginBottom: 16,
    background: "rgba(220,38,38,0.12)",
    border: "1px solid rgba(220,38,38,0.35)",
    color: "#fecaca",
    padding: 14,
    borderRadius: 12,
  },
  reportCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "end",
    flexWrap: "wrap",
    background: "linear-gradient(135deg, rgba(49,46,129,0.72), rgba(15,118,110,0.58))",
    border: "1px solid rgba(165,180,252,0.28)",
    borderRadius: 18,
    padding: 18,
    marginBottom: 18,
  },
  reportTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 900,
  },
  reportSubtitle: {
    margin: "6px 0 0",
    color: "#c7d2fe",
    lineHeight: 1.5,
    fontSize: 13,
  },
  reportControls: {
    display: "flex",
    alignItems: "end",
    gap: 12,
    flexWrap: "wrap",
  },
  card: {
    background: "rgba(15,23,42,0.72)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 18,
    padding: 20,
    marginBottom: 20,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 800,
  },
  subsectionTitle: {
    margin: "22px 0 12px",
    fontSize: 18,
    fontWeight: 800,
  },
  infoBox: {
    marginTop: 14,
    marginBottom: 18,
    padding: 14,
    borderRadius: 12,
    background: "rgba(79,70,229,0.12)",
    border: "1px solid rgba(99,102,241,0.3)",
    color: "#c7d2fe",
    lineHeight: 1.5,
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  grid5: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 14,
    marginBottom: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  label: {
    fontSize: 13,
    color: "#cbd5e1",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    background: "#0f172a",
    border: "1px solid #334155",
    color: "white",
    padding: "12px 14px",
    borderRadius: 12,
    outline: "none",
  },
  buttonRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 18,
  },
  primaryBtn: {
    background: "#4f46e5",
    color: "white",
    border: "1px solid #6366f1",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "#1d4ed8",
    color: "white",
    border: "1px solid #3b82f6",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
  ghostBtn: {
    background: "transparent",
    color: "#e2e8f0",
    border: "1px solid #475569",
    borderRadius: 12,
    padding: "12px 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 14,
    marginBottom: 20,
  },
  summaryCard: {
    background: "rgba(2,6,23,0.55)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 14,
    padding: 14,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: 800,
    marginBottom: 10,
  },
  summaryLine: {
    color: "#cbd5e1",
    fontSize: 14,
    marginBottom: 6,
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid rgba(148,163,184,0.16)",
    borderRadius: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: 12,
    background: "rgba(15,23,42,0.95)",
    color: "#cbd5e1",
    fontWeight: 800,
    fontSize: 13,
    borderBottom: "1px solid rgba(148,163,184,0.18)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: 12,
    borderBottom: "1px solid rgba(148,163,184,0.12)",
    verticalAlign: "top",
    color: "#f8fafc",
    fontSize: 14,
  },
  nowrapCell: {
    whiteSpace: "nowrap",
  },
  cellPrimary: {
    display: "inline-block",
    fontWeight: 650,
    lineHeight: 1.45,
  },
  componentSummary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "3px 0",
    lineHeight: 1.35,
  },
  plannedTotal: {
    display: "block",
    marginBottom: 3,
    fontSize: 15,
    whiteSpace: "nowrap",
  },
  weightBasis: {
    display: "block",
    color: "#71847a",
    lineHeight: 1.4,
    marginBottom: 7,
  },
  componentPlanList: {
    display: "grid",
    gap: 3,
  },
  componentPlanRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 12,
    lineHeight: 1.35,
  },
  actualComponentRow: {
    display: "grid",
    gridTemplateColumns: "minmax(58px, 1fr) 96px 14px",
    alignItems: "center",
    gap: 7,
    marginBottom: 8,
  },
  actualComponentName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 650,
  },
  actualInput: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    background: "#fff",
    border: "1px solid #c8d8d0",
    color: "#243b31",
    padding: "9px 10px",
    borderRadius: 10,
    outline: "none",
    fontSize: 14,
    fontWeight: 650,
  },
  actualValue: {
    textAlign: "right",
  },
  actualUnit: {
    color: "#60756b",
    fontWeight: 700,
  },
  singleActualRow: {
    display: "grid",
    gridTemplateColumns: "110px 14px",
    alignItems: "center",
    gap: 7,
  },
  emptyCell: {
    padding: 18,
    textAlign: "center",
    color: "#94a3b8",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 800,
    minWidth: 90,
  },
  actionStack: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(96px, 1fr))",
    gap: 8,
    width: "100%",
  },
  actionBtnPrimary: {
    width: "100%",
    background: "#16a34a",
    color: "white",
    border: "1px solid #22c55e",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 700,
  },
  actionBtnWarn: {
    width: "100%",
    background: "#dc2626",
    color: "white",
    border: "1px solid #ef4444",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 700,
  },
  actionBtnMuted: {
    width: "100%",
    background: "#334155",
    color: "white",
    border: "1px solid #475569",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 700,
  },
  actionBtnSecondary: {
    width: "100%",
    background: "#7c3aed",
    color: "white",
    border: "1px solid #8b5cf6",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 700,
  },
};
